import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { RefreshCw, Search } from 'lucide-react';
import {
  RMA_PAGE_SIZE,
  RMA_PAGE_SIZE_OPTIONS,
  useDeviceRmaRequests,
  useSyncDeviceRmaRequests,
} from '../hooks/useRma';
import type { DeviceRmaRequest } from '@staff-app/shared-types';
import { DATA_TABLE_WRAPPER } from '@/lib/data-table';
import { RmaTable } from '../components/RmaTable';
import { RmaDetailDrawer } from '../components/RmaDetailDrawer';
import { RmaVerifyDialog } from '../components/RmaVerifyDialog';
import { RmaReturnDialog } from '../components/RmaReturnDialog';
import { RmaDismissDialog } from '../components/RmaDismissDialog';

const ALL = '__all__';
const MODELS = [
  { value: 'OPTISTICK', label: 'OptiStick' },
  { value: 'PRO', label: 'Pro Player' },
  { value: 'PROMAX', label: 'ProMax Player' },
  // Listed on purpose, not hidden as an edge case: it is the filter someone reaches for to find
  // the rows whose model could not be read off the subject and needs a human eye.
  { value: 'UNKNOWN', label: 'Unknown model' },
];

/**
 * RMA Requests — the first section of Devices Fulfillment (DevicesFulfillmentShell owns the h1).
 *
 * It leads with the QUEUE and has no create card, the opposite of its Demo Coupons sibling. There
 * is nothing to compose here: every row arrives from a customer submitting a HubSpot RMA form, and
 * the only act available on this page is deciding whether a request is eligible. So the work list
 * IS the page, and it opens filtered to the requests nobody has checked yet.
 *
 * The default filter is the point of the design. An RMA queue's failure mode is a request sitting
 * unread for a week, and a page that opens on "everything ever submitted" hides exactly that. The
 * filter is still a filter — the operator can widen it in one click — but the landing view is the
 * work.
 */
export function RmaPage() {
  const [search, setSearch] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  // Tri-state, and it starts at `false` — the unverified queue. `undefined` is "show everything".
  const [verified, setVerified] = useState<boolean | undefined>(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<number>(RMA_PAGE_SIZE);
  // Owned by the PAGE, not the row: a drawer held by a row dies when the list refetches, and the
  // list refetches on every verification.
  const [open, setOpen] = useState<DeviceRmaRequest | null>(null);
  // Also page-owned, and for the same reason: the list refetches on every verification, so a
  // dialog held by a row would be unmounted mid-decision.
  const [verifying, setVerifying] = useState<DeviceRmaRequest | null>(null);
  const [returning, setReturning] = useState<DeviceRmaRequest | null>(null);
  const [dismissing, setDismissing] = useState<DeviceRmaRequest | null>(null);
  /**
   * `undefined` = the working queue, `true` = the dismissed review view.
   *
   * Two values rather than the tri-state the verification filter uses, and deliberately: "all
   * requests including dismissed" is a view nobody asked for and would quietly undo the point of
   * dismissing — a duplicate would be back in the operator's way the moment they widened the model
   * filter.
   */
  const [showDismissed, setShowDismissed] = useState(false);

  useEffect(() => {
    setPageIndex(0);
    // Page size resets the position too: staying on page 7 while the pages get bigger lands the
    // operator somewhere they did not ask to be.
  }, [search, deviceModel, verified, showDismissed, pageSize]);

  const { data, loading, error, refetch } = useDeviceRmaRequests(
    search,
    deviceModel,
    verified,
    pageIndex * pageSize,
    pageSize,
    showDismissed || undefined,
  );
  const page = data?.deviceRmaRequests;
  const items = page?.items ?? [];

  /**
   * KEEP AN OPEN PANEL IN STEP WITH THE LIST.
   *
   * `open` / `verifying` / `returning` hold the row OBJECT that was current when the panel was
   * opened — page-owned, so a refetch cannot unmount them mid-decision. The cost of that is
   * staleness: every mutation in those panels refetches the list, and without this the panel goes
   * on rendering the row as it was BEFORE the write. It showed: the "Create RMA order" button
   * stayed on screen after an order had been created (requester, 2026-09-21), and the same
   * staleness would have hidden a freshly corrected address or a just-recorded tracking number.
   *
   * Re-points each open panel at the row with the same submission id from the newest data, and
   * ONLY when one is found: verifying a row removes it from the unverified queue, and a panel that
   * closed itself the instant its work succeeded would take the operator's next step with it.
   */
  useEffect(() => {
    const fresh = (r: DeviceRmaRequest | null) =>
      r ? (items.find((i) => i.submissionId === r.submissionId) ?? r) : r;
    setOpen((r) => fresh(r));
    setVerifying((r) => fresh(r));
    setReturning((r) => fresh(r));
    // `items` is a new array each render; the identity of the ROWS is what matters, and Apollo
    // returns the same row objects when nothing changed, so this settles rather than looping.
  }, [items]);

  const total = page?.total ?? 0;
  const firstShown = total === 0 ? 0 : pageIndex * pageSize + 1;
  const lastShown = Math.min(total, pageIndex * pageSize + items.length);

  const [syncNow, { loading: syncing }] = useSyncDeviceRmaRequests();
  // Kept in page state and rendered, because the server's refusal IS the diagnosis. A
  // misconfigured environment ("no forms allowlisted", "HubSpot not configured") is the difference
  // between "nothing submitted yet" and "this will never work", and the two look identical on an
  // empty table. Shown verbatim, like every other server message in this app.
  const [pollError, setPollError] = useState<string | null>(null);
  const pollNow = async () => {
    setPollError(null);
    try {
      await syncNow();
      await refetch();
    } catch (e) {
      setPollError((e as Error).message);
    }
  };

  // The verification filter is a Select rather than a checkbox because it is genuinely three
  // states, and a checkbox can only offer two. `undefined` cannot be a SelectItem value, so the
  // three are carried as strings and mapped at the boundary.
  const verifiedValue = verified === undefined ? ALL : verified ? 'yes' : 'no';
  const onVerifiedChange = (v: string) =>
    setVerified(v === ALL ? undefined : v === 'yes');

  return (
    <div className="flex h-full flex-col gap-6 overflow-hidden p-6" data-testid="rma-page">
      {/* No heading: the shell owns the page title, and the active tab already names this
          section. Only what is specific to RMA earns space here. */}
      <p className="text-sm text-muted-foreground">
        {'Return and exchange requests customers submitted through the HubSpot RMA forms, polled '}
        {'every five minutes. HubSpot stays the record of the conversation — what this page adds '}
        {'is the eligibility check: warranty window, order number, and the receipt they attached.'}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[280px] max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search submission, subject, customer, order, ship-to, invoice id…"
            className="pl-9"
            data-testid="rma-search"
          />
        </div>

        {/* THE DISMISSED VIEW. Its own control rather than another option on the verification
            filter: they answer different questions ("what still needs checking" vs "what did we
            take out of the queue"), and folding them together would make "All requests" silently
            include duplicates. */}
        <Select
          value={showDismissed ? 'dismissed' : 'open'}
          onValueChange={(v) => setShowDismissed(v === 'dismissed')}
        >
          <SelectTrigger className="w-[11rem]" data-testid="rma-dismissed-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">{'Working queue'}</SelectItem>
            <SelectItem value="dismissed">{'Dismissed'}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={verifiedValue} onValueChange={onVerifiedChange}>
          <SelectTrigger className="w-[190px]" data-testid="rma-filter-verified">
            <SelectValue placeholder="Verification" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="no">{'Needs verification'}</SelectItem>
            <SelectItem value="yes">{'Verified'}</SelectItem>
            <SelectItem value={ALL}>{'All requests'}</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={deviceModel || ALL}
          onValueChange={(v) => setDeviceModel(v === ALL ? '' : v)}
        >
          <SelectTrigger className="w-[180px]" data-testid="rma-filter-model">
            <SelectValue placeholder="Model" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'All models'}</SelectItem>
            {MODELS.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          className="gap-1"
          disabled={syncing}
          onClick={() => {
            void pollNow();
          }}
          data-testid="rma-poll-now"
          title="Poll HubSpot now instead of waiting for the five-minute timer"
        >
          <RefreshCw className={`h-4 w-4${syncing ? ' animate-spin' : ''}`} />
          {syncing ? 'Polling…' : 'Poll now'}
        </Button>
      </div>

      {pollError ? (
        <div
          className="rounded-md border border-destructive p-3 text-sm text-destructive"
          data-testid="rma-poll-error"
        >
          {pollError}
        </div>
      ) : null}

      {/* min-h-0 + flex-1: this region absorbs the leftover height so the table scrolls INSIDE
          itself, leaving the description, the filters and the pager put. Without min-h-0 a flex
          child refuses to shrink below its content and the whole page scrolls instead — the same
          note ShopifyOrdersPage carries, which is the table this one is meant to match. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {error ? (
          <div
            className="rounded-md border border-destructive p-4 text-sm text-destructive"
            data-testid="rma-error"
          >
            {error.message}
          </div>
        ) : (
          <>
            <div className="flex shrink-0 items-center gap-3">
              <div className="text-xs text-muted-foreground" data-testid="rma-count">
                {loading && !page
                  ? 'Loading…'
                  : total > items.length
                    ? `${firstShown}–${lastShown} of ${total} requests`
                    : `${total} request${total === 1 ? '' : 's'}`}
              </div>

              {/* THE SAME CONTROL AS INTERNATIONAL REQUESTS AND THE DEVICES LIST (requester,
                  2026-09-22): the `Rows per page` label said once to the left, bare numbers in the
                  list, a 72px h-8 trigger, `cursor-pointer` throughout and an `aria-label` so the
                  select is still named when the visible label sits beside it. Copied rather than
                  re-decided — two paginated staff tables that page differently is the kind of small
                  inconsistency that makes a console feel assembled from parts.

                  Beside the count, not down with Previous/Next: "how many am I looking at" and
                  "how many should I see" are the same question, and those buttons disappear
                  entirely when everything fits on one page. */}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{'Rows per page'}</span>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger
                    className="h-8 w-[72px] cursor-pointer"
                    aria-label={'Rows per page'}
                    data-testid="rma-page-size"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RMA_PAGE_SIZE_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)} className="cursor-pointer">
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* THE TABLE OWNS BOTH AXES, and it has to for the header to freeze.
                `overflow-x-auto` alone did not mean "horizontal only": per CSS, if one axis is not
                `visible` the other computes from `visible` to `auto`, so this div was ALREADY a
                vertical scroll container — just an unbounded one that never scrolled. That made it
                the sticky header's nearest scrolling ancestor, which is why `DATA_TABLE_HEADER`'s
                `sticky top-0` did nothing: the page scrolled the whole box away, header included.
                Bounding the height is what gives the header something to stick to. */}
            <div className="flex min-h-0 flex-1 flex-col">
              <div className={DATA_TABLE_WRAPPER}>
                <RmaTable
                  requests={items}
                  onOpen={setOpen}
                  onVerify={setVerifying}
                  onReturn={setReturning}
                />
              </div>
            </div>
            {total > pageSize && (
              <div className="flex shrink-0 items-center justify-end gap-2 pt-1">
                <span className="text-xs text-muted-foreground" data-testid="rma-page-label">
                  {`Page ${pageIndex + 1} of ${Math.max(1, Math.ceil(total / pageSize))}`}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageIndex === 0 || loading}
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  data-testid="rma-page-prev"
                >
                  {'Previous'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={lastShown >= total || loading}
                  onClick={() => setPageIndex((p) => p + 1)}
                  data-testid="rma-page-next"
                >
                  {'Next'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {open && (
        <RmaDetailDrawer
          row={open}
          onClose={() => setOpen(null)}
          onDismiss={() => setDismissing(open)}
        />
      )}
      {dismissing && (
        <RmaDismissDialog row={dismissing} onClose={() => setDismissing(null)} />
      )}

      {verifying && (
        <RmaVerifyDialog row={verifying} onClose={() => setVerifying(null)} />
      )}
      {returning && (
        <RmaReturnDialog row={returning} onClose={() => setReturning(null)} />
      )}
    </div>
  );
}
